import { InjectedProperty } from "../../../Global/Base/DIContainer";
import { Event } from "../../../Global/Base/Events";
import { GlobalContext } from "../../../Global/Base/GlobalContext";
import { IFileStreamReader } from "../../../Global/Base/Streams/IFileStreamReader";
import { OWObject } from "../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { FileUploader } from "../../../OW.Remoting/Remoting/FileUploader";
import { RequestContext } from "../../../OW.Remoting/Remoting/RequestContext";
/**
 * Třída umožňuje nahrávat soubory do DMS
 * @includeToDoc
 */
export interface IUploadManager {
    readonly onUploadRegistered: Event<UploadManager, IUploadManagerInfo>;

    readonly onUploadStart: Event<UploadManager, IUploadManagerInfo>;

    readonly onUploadProgress: Event<UploadManager, IUploadManagerInfo>;

    readonly onUploadError: Event<UploadManager, IUploadManagerInfo>;

    readonly onUploadCancel: Event<UploadManager, IUploadManagerInfo>;

    readonly onUploadComplete: Event<UploadManager, IUploadManagerInfo>;

    /**
     * Nastavení zda má být upload ze seznamu odstraněn po jeho dokončení
     */
    removeAfterFinish: boolean;

    /**
     * Nastavení zda má být upload ze seznamu odstraněn pokud skončí chybou
     */
    removeAfterError: boolean;

    /**
     * Nastavení zda má být upload ze seznamu odstraněn pokud je zrušen uživatelem
     */
    removeAfterCancel: boolean;

    /**
     * Pokud je na true, provede se po dokončení uploadu UpdateInstance na daném objektu
     */
    updateInstanceAfterUpload: boolean;

    /**
     * Informace o tom jestli probiha nejaky upload
     */
    readonly isUploading: boolean;

    /**
     * Příprava dat pro budoucí upload - umožňuje naslouchat událostem a změnám uploadu, je možné volat opakovaně
     * @param instanceId instance objektu, pro kterou se má registrovat upload
     * @param attrName název atributu objektu, pro který se má registrovat upload
     * @param customData volitelná data
     * @param requestContext Viz {@link RequestContext}
     */
    registerUpload(instanceId: string, attrName: string, customData?: any, requestContext?: RequestContext): IUploadManagerInfo;

    /**
     * Spustí nahrávání souboru
     * @param streamReader reader umožňující číst data k nahrání
     * @param object objekt, do kterého se má nahrávání provést
     * @param attrName název atributu objektu, do kterého se má nahrávání provést
     * @param hashProvider funkce, která vrátí hash nahrávaného souboru
     * @param requestContext Viz {@link RequestContext}
     */
    startUpload(streamReader: IFileStreamReader, object: OWObject, attrName: string, hashProvider?: () => string, requestContext?: RequestContext): Promise<void>;

    /**
     * Vrátí data s informacemi o uploadu, null pokud takový ještě nebyl zaregistrován
     * @param instanceId instance objektu, pro kterou se hledají data
     * @param attrName název atributu objektu, pro který se má registrovat upload
     */
    getUploadData(instanceId: string, attrName: string): IUploadManagerInfo;

    /**
     * Zruší a vyresetuje všechny aktivní (probíhající nebo v chybovém stavu) uploady na dané třídě.
     * Pokud je specifikováno i objectId, tak se zruší uploady jen tohoto id
     * @param className Název třídy, pro kterou se mají zrušit všechny uploady
     * @param objectId Id objektu, pro který se mají zrušit uploady - pokud je specifikováno, jinak všechny
     */
    stopUploads(className: string, objectId?: string | null): void;

    /**
     * Vratí true pokud na dané třídě probíhá upload
     * @param className název třídy
     */
    isUploadRunningOnClass(className: string): boolean;

    removeUploadData(uploadData: IUploadManagerInfo): void;
}

export class IUploadManager {
    private static _properties = ["onUploadRegistered", "onUploadStart", "onUploadProgress", "onUploadError", "onUploadCancel", "onUploadComplete",
        "removeAfterFinish", "removeAfterError", "removeAfterCancel", "updateInstanceAfterUpload", "isUploading"];
    private static _methods = ["registerUpload", "startUpload", "getUploadData", "stopUploads", "isUploadRunningOnClass", "removeUploadData"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

export interface IUploadManagerInfo {
    uploader: FileUploader;
    attrName: string;
    className: string;
    customData: any;
    object: OWObject;
    lastError: string;
}

export class UploadManager {
    private static get _instance(): IUploadManager {
        return {} as any;
    }

    public static get instance(): IUploadManager {
        if (self !== top)
            return GlobalContext.getType<typeof UploadManager>(UploadManager).instance;

        return UploadManager._instance;
    }
}

InjectedProperty.bindProperty(UploadManager, "_instance", IUploadManager);
GlobalContext.registerType(UploadManager);
